import { useState, useEffect, useCallback, useRef } from 'react';
import { INCIDENTS_API } from '@/lib/incidentsApi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, ExternalLink, Sparkles, AlertCircle, RefreshCw } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { usePermissions } from '@/contexts/PermissionsContext';
import { permissionDeniedTitle } from '@/lib/permissionUi';
import {
  isChatMirroredWorkflowTicket,
  isWorkflowTicket,
  isPendingApproval,
  getWorkflowStageLabel,
  isFfidSoftHumanHold,
  WORKFLOW_STAGE,
  WORKFLOW_STAGE_LABELS,
} from '@/lib/workflowHelpers';
import { fetchTicketSummaries, TICKETS_PAGE_LIMIT } from '@/lib/ticketsApi';

const API = INCIDENTS_API;

const TICKETS_POLL_MS = 6000;

function workflowStageBadgeClass(stage, ticket) {
  const label = String(
    (ticket && getWorkflowStageLabel(ticket)) || WORKFLOW_STAGE_LABELS[stage] || '',
  ).toLowerCase();
  // End-user reported FFID access issue — critical red.
  if (label.includes('access issue') || label.includes('sap login issue')) {
    return 'bg-cp-crit text-white';
  }
  // Soft FFID holds — yellow/amber.
  if (
    label.includes('human escalation') ||
    label.includes('firefighter already assigned') ||
    label.includes('no firefighter id available') ||
    label.includes('form missing') ||
    label.includes('invalid access window')
  ) {
    return 'bg-cp-mod text-white';
  }
  switch (stage) {
    case WORKFLOW_STAGE.PENDING_APPROVAL:
      return 'bg-cp-imp text-white';
    case WORKFLOW_STAGE.NEEDS_INFO:
      return 'bg-cp-mod text-white';
    case WORKFLOW_STAGE.DONE:
      return 'bg-cp-ok text-white';
    case WORKFLOW_STAGE.SCHEDULED:
      return 'bg-cp-mod text-white';
    case WORKFLOW_STAGE.REJECTED:
    case WORKFLOW_STAGE.FAILED:
      return 'bg-cp-crit text-white';
    case WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED:
      return 'bg-cp-crit text-white';
    case WORKFLOW_STAGE.ESCALATED:
      return 'bg-cp-imp text-white';
    case WORKFLOW_STAGE.PROVISIONING:
    case WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION:
      return 'bg-cp-violet text-white';
    default:
      return 'bg-cp-violet text-white';
  }
}

/** Card chrome: left accent + optional live glow (open / pending approval). */
function ticketCardClass(ticket) {
  const base =
    'relative overflow-hidden cursor-pointer transition-[transform,box-shadow,border-color] duration-150 hover:-translate-y-px';

  if (isPendingApproval(ticket)) {
    return `${base} ticket-card ticket-card--approval`;
  }

  if (isWorkflowTicket(ticket)) {
    const stage = ticket.workflow_stage;
    if (stage === WORKFLOW_STAGE.NEEDS_INFO) {
      return `${base} ticket-card ticket-card--needs-info`;
    }
    if (stage === WORKFLOW_STAGE.DONE) {
      return `${base} ticket-card ticket-card--done`;
    }
    if (stage === WORKFLOW_STAGE.SCHEDULED) {
      return `${base} ticket-card ticket-card--needs-info`;
    }
    if (stage === WORKFLOW_STAGE.ACCESS_ISSUE_REPORTED) {
      // Soft holds (email / future window) stay amber; end-user ISSUE is critical red.
      if (isFfidSoftHumanHold(ticket)) {
        return `${base} ticket-card ticket-card--needs-info`;
      }
      return `${base} ticket-card ticket-card--failed`;
    }
    if (stage === WORKFLOW_STAGE.REJECTED || stage === WORKFLOW_STAGE.FAILED) {
      return `${base} ticket-card ticket-card--failed`;
    }
    if (
      stage === WORKFLOW_STAGE.PROVISIONING ||
      stage === WORKFLOW_STAGE.CHECKING ||
      stage === WORKFLOW_STAGE.EXTRACTING ||
      stage === WORKFLOW_STAGE.AWAITING_USER_CONFIRMATION
    ) {
      return `${base} ticket-card ticket-card--workflow-active`;
    }
    return `${base} ticket-card ticket-card--workflow`;
  }

  const status = (ticket.status || '').toLowerCase();
  if (status === 'open') {
    return `${base} ticket-card ticket-card--open`;
  }

  const byStatus = {
    open: 'ticket-card ticket-card--open',
    in_analysis: 'ticket-card ticket-card--analysis',
    in_design: 'ticket-card ticket-card--design',
    in_development: 'ticket-card ticket-card--dev',
    in_testing: 'ticket-card ticket-card--test',
    in_deployment: 'ticket-card ticket-card--deploy',
    in_governance: 'ticket-card ticket-card--gov',
    completed: 'ticket-card ticket-card--done',
    rejected: 'ticket-card ticket-card--failed',
  };

  return `${base} ${byStatus[status] || 'ticket-card ticket-card--default'}`;
}

export default function Tickets() {
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission('tickets', 'create');
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [configuredSystems, setConfiguredSystems] = useState([]);
  const [assignedAgentsMap, setAssignedAgentsMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [datePreset, setDatePreset] = useState('');
  const offsetRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const listGenerationRef = useRef(0);
  const appendAbortRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    ticket_number: '',
    source: 'local',
    title: '',
    description: '',
    priority: 'medium',
    requester_name: '',
    requester_email: '',
    sap_system: 'S/4HANA Cloud',
    target_system_id: '',
    target_system_name: '',
    customization_type: 'Enhancement',
    is_error_ticket: false,
    error_code: '',
    error_message: '',
    error_module: '',
    chain_of_thought: ''
  });

  const filterParams = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (statusFilter) params.status = statusFilter;
    if (priorityFilter) params.priority = priorityFilter;
    if (datePreset === '7d' || datePreset === '30d') {
      const days = datePreset === '7d' ? 7 : 30;
      const from = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
      params.created_from = from.toISOString();
    }
    return params;
  }, [q, statusFilter, priorityFilter, datePreset]);

  // Soft poll: refresh page-1 rows only. Never reset offset/hasMore or the list feels like a loop.
  const mergePageOne = (incoming) => {
    setTickets((prev) => {
      const filtered = (incoming || []).filter((t) => !isChatMirroredWorkflowTicket(t));
      const page1Ids = new Set(filtered.map((t) => t.ticket_number).filter(Boolean));
      const deeper = prev.filter((t) => t?.ticket_number && !page1Ids.has(t.ticket_number));
      return [...filtered, ...deeper];
    });
  };

  const abortInFlightAppend = () => {
    if (appendAbortRef.current) {
      appendAbortRef.current.abort();
      appendAbortRef.current = null;
    }
    loadingMoreRef.current = false;
    setLoadingMore(false);
  };

  useEffect(() => {
    fetchTickets({ reset: true, includeTotal: true });
    fetchConfiguredSystems();
    // Poll page 1 only — never include_total on the interval (COUNT storm).
    // Pause while the tab is hidden (industry hardening; cuts idle Dev2 QPS).
    const tick = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      fetchTickets({ reset: false, includeTotal: false });
    };
    const interval = setInterval(tick, TICKETS_POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchTickets({ reset: false, includeTotal: false });
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      abortInFlightAppend();
    };
  }, [q, statusFilter, priorityFilter, datePreset]);

  const fetchConfiguredSystems = async () => {
    // settings/sap-landscape/systems: not called on tickets page (disabled)
    setConfiguredSystems([]);
  };

  const fetchTickets = async ({ reset = true, includeTotal = false } = {}) => {
    const generation = reset
      ? (++listGenerationRef.current)
      : listGenerationRef.current;
    try {
      if (reset) {
        abortInFlightAppend();
        offsetRef.current = 0;
        setHasMore(false);
        setLoading(true);
      }
      const page = await fetchTicketSummaries(API, {
        limit: TICKETS_PAGE_LIMIT,
        offset: 0,
        includeTotal,
        ...filterParams(),
      });
      if (generation !== listGenerationRef.current) return;
      if (reset) {
        setTickets(page.items.filter((t) => !isChatMirroredWorkflowTicket(t)));
        setHasMore(page.has_more);
        offsetRef.current = TICKETS_PAGE_LIMIT;
        if (includeTotal && typeof page.total === 'number') setTotal(page.total);
        setAssignedAgentsMap({});
      } else {
        // Soft poll must not rewind offset; discard page-1 has_more (locked rule 2).
        mergePageOne(page.items);
      }
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return;
      }
      console.error('Error fetching tickets:', error);
    } finally {
      if (generation === listGenerationRef.current) setLoading(false);
    }
  };

  const loadMoreTickets = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);
    const generation = listGenerationRef.current;
    const controller = new AbortController();
    appendAbortRef.current = controller;
    try {
      const page = await fetchTicketSummaries(API, {
        limit: TICKETS_PAGE_LIMIT,
        offset: offsetRef.current,
        includeTotal: false,
        signal: controller.signal,
        ...filterParams(),
      });
      if (generation !== listGenerationRef.current) return;
      const rows = page.items.filter((t) => !isChatMirroredWorkflowTicket(t));
      setTickets((prev) => {
        const seen = new Set(prev.map((t) => t.ticket_number));
        return [...prev, ...rows.filter((t) => t?.ticket_number && !seen.has(t.ticket_number))];
      });
      setHasMore(page.has_more);
      // Advance by requested page size (server offset), not filtered client count.
      offsetRef.current += TICKETS_PAGE_LIMIT;
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || error?.name === 'AbortError') {
        return;
      }
      console.error('Error loading more tickets:', error);
    } finally {
      if (appendAbortRef.current === controller) appendAbortRef.current = null;
      if (generation === listGenerationRef.current) {
        loadingMoreRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [filterParams, hasMore]);

  // Append next page when the bottom sentinel enters view (one continuous list, not a loop).
  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el || !hasMore) return undefined;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) void loadMoreTickets();
      },
      { root: null, rootMargin: '160px 0px', threshold: 0 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadMoreTickets, tickets.length]);

  const filtersActive = Boolean(q || statusFilter || priorityFilter || datePreset);
  const applySearch = (e) => {
    e?.preventDefault?.();
    setQ((searchInput || '').trim());
  };


  const createMockTicket = async () => {
    if (!canCreate) {
      toast.error(permissionDeniedTitle(false, 'create tickets'));
      return;
    }
    try {
      const response = await axios.post(`${API}/tickets/mock`);
      console.log('Mock ticket created:', response.data);
      alert(`✅ Mock ticket created: ${response.data.ticket_number}`);
      await fetchTickets({ reset: true, includeTotal: true });
    } catch (error) {
      console.error('Error creating mock ticket:', error);
      alert(`❌ Error creating mock ticket: ${error.response?.data?.detail || error.message}`);
    }
  };

  const createTicket = async () => {
    if (!canCreate) {
      toast.error(permissionDeniedTitle(false, 'create tickets'));
      return;
    }
    try {
      await axios.post(`${API}/tickets`, formData);
      setOpen(false);
      fetchTickets({ reset: true, includeTotal: true });
      setFormData({
        ticket_number: '',
        source: 'local',
        title: '',
        description: '',
        priority: 'medium',
        requester_name: '',
        requester_email: '',
        sap_system: 'S/4HANA Cloud',
        target_system_id: '',
        target_system_name: '',
        customization_type: 'Enhancement',
        is_error_ticket: false,
        error_code: '',
        error_message: '',
        error_module: '',
        chain_of_thought: ''
      });
    } catch (error) {
      console.error('Error creating ticket:', error);
    }
  };

  const handleSystemSelection = (systemId) => {
    const selectedSystem = configuredSystems.find(s => s.id === systemId);
    setFormData({
      ...formData,
      target_system_id: systemId,
      target_system_name: selectedSystem ? selectedSystem.name : '',
      sap_system: selectedSystem ? selectedSystem.type : formData.sap_system
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      open: 'bg-[#a855f7] text-white',
      in_analysis: 'bg-cp-violet text-white',
      in_design: 'bg-[#a855f7] text-white',
      in_development: 'bg-cp-violet text-white',
      in_testing: 'bg-cp-mod text-cp-fg',
      in_deployment: 'bg-cp-imp text-white',
      in_governance: 'bg-[#d946ef] text-white',
      completed: 'bg-cp-ok text-white',
      rejected: 'bg-cp-crit text-white',
    };
    return colors[status] || 'bg-cp-surface2 text-cp-muted';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'border-cp-faint text-cp-muted',
      medium: 'border-cp-violet text-cp-violet',
      high: 'border-cp-imp text-cp-imp',
      critical: 'border-cp-crit text-cp-crit',
    };
    return colors[priority] || 'border-cp-faint text-cp-muted';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-lg">Loading...</div></div>;
  }

  return (
    <div className="space-y-6" data-testid="tickets-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Tickets</h1>
          <p className="text-muted-foreground mt-2">
            Manage and View all tickets
            {typeof total === 'number' ? ` · ${total} matching` : ''}
          </p>
        </div>
      </div>

      <form
        className="flex flex-wrap gap-3 items-end"
        onSubmit={applySearch}
        data-testid="tickets-filters"
      >
        <div className="flex-1 min-w-[200px]">
          <Label htmlFor="ticket-search" className="text-xs text-muted-foreground">Search</Label>
          <Input
            id="ticket-search"
            placeholder="Ticket number or title"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </div>
        <div className="w-[160px]">
          <Label className="text-xs text-muted-foreground">Status</Label>
          <Select
            value={statusFilter || '__all__'}
            onValueChange={(v) => setStatusFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="in_analysis">In analysis</SelectItem>
              <SelectItem value="in_development">In development</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[140px]">
          <Label className="text-xs text-muted-foreground">Priority</Label>
          <Select
            value={priorityFilter || '__all__'}
            onValueChange={(v) => setPriorityFilter(v === '__all__' ? '' : v)}
          >
            <SelectTrigger><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All</SelectItem>
              <SelectItem value="low">Low</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-[140px]">
          <Label className="text-xs text-muted-foreground">Created</Label>
          <Select
            value={datePreset || '__all__'}
            onValueChange={(v) => setDatePreset(v === '__all__' ? '' : v)}
          >
            <SelectTrigger><SelectValue placeholder="Any time" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Any time</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button type="submit" variant="secondary">Search</Button>
      </form>

      <div className="mt-4">
          {tickets.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground mb-4" data-testid="tickets-empty-state">
                  {filtersActive
                    ? 'No tickets match these filters.'
                    : 'No tickets found. Create a new ticket to get started.'}
                </p>
                {!filtersActive && (
                  <div className="flex gap-2">
                    <span title={permissionDeniedTitle(canCreate, 'create tickets')}>
                      <Button onClick={createMockTicket} disabled={!canCreate}>Generate Mock Ticket</Button>
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4">
              {tickets.map((ticket) => (
                <Card
                  key={ticket.id}
                  className={ticketCardClass(ticket)}
                  onClick={() => navigate(`/tickets/${ticket.ticket_number}`)}
                  data-testid={`ticket-card-${ticket.id}`}
                >
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <Badge variant="outline" className="font-mono text-xs">{ticket.ticket_number}</Badge>
                          <Badge variant="outline" className="text-xs">{ticket.source}</Badge>
                          {/* Workflow tickets: stage badge alone (avoids "completed" + "Completed"). */}
                          {isWorkflowTicket(ticket) && ticket.workflow_stage ? (
                            <Badge className={`text-xs ${workflowStageBadgeClass(ticket.workflow_stage, ticket)}`}>
                              {getWorkflowStageLabel(ticket)}
                            </Badge>
                          ) : (
                            <Badge className={`text-xs ${getStatusColor(ticket.status)}`}>
                              {ticket.status.replace('_', ' ')}
                            </Badge>
                          )}
                          <Badge variant="outline" className={`text-xs border ${getPriorityColor(ticket.priority)}`}>
                            {ticket.priority}
                          </Badge>
                        </div>
                        <CardTitle className="text-xl line-clamp-2" title={ticket.title}>
                          {ticket.title}
                        </CardTitle>
                        <CardDescription className="mt-2">{ticket.description}</CardDescription>
                      </div>
                      <ExternalLink className="h-5 w-5 text-muted-foreground shrink-0" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <div className="text-muted-foreground">Target System</div>
                          <div className="font-medium">
                            {ticket.target_system_name || ticket.sap_system}
                          </div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Type</div>
                          <div className="font-medium">{ticket.customization_type}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Requester</div>
                          <div className="font-medium">{ticket.requester_name}</div>
                        </div>
                        <div>
                          <div className="text-muted-foreground">Created</div>
                          <div className="font-medium">{new Date(ticket.created_at).toLocaleDateString()}</div>
                        </div>
                      </div>
                      
                      {/* Assigned Agents from Agent Studio */}
                      {assignedAgentsMap[ticket.id] && assignedAgentsMap[ticket.id].length > 0 && (
                        <div className="pt-2 border-t">
                          <div className="text-xs text-muted-foreground mb-1">Custom Agents:</div>
                          <div className="flex flex-wrap gap-1">
                            {assignedAgentsMap[ticket.id].map((assignment, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                <Sparkles className="h-3 w-3 mr-1" />
                                {assignment.agent_name}
                                {assignment.status === 'completed' && ' ✓'}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          {tickets.length > 0 && hasMore && (
            <div
              ref={loadMoreSentinelRef}
              className="flex justify-center py-6"
              data-testid="tickets-load-more-sentinel"
            >
              {loadingMore ? (
                <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  Loading more…
                </span>
              ) : (
                <span className="h-px w-px" aria-hidden />
              )}
            </div>
          )}
          {!loading && tickets.length > 0 && !hasMore && !loadingMore && (
            <p
              className="py-6 text-center text-sm text-muted-foreground"
              data-testid="tickets-end-of-list"
            >
              You’ve reached the end
            </p>
          )}
      </div>
    </div>
  );
}
